import { usePayItemSelectPayItemNoGeometriesQuery } from 'schema'
import { useOptions } from '../../hooks/useOptions'
import { DefaultQueryHookOptions } from 'utils/defaults'
import { useGlobalState } from 'context/GlobalStateContext'
import { createOptionalContext } from 'utils/createContext'
import { toLabeledValues as toValues } from '../../utils/labeledValues'
import { PayItemSelectContext } from './types'

/**
 * @see {@link ToLabeledValuesOptions}
 */
const toLabeledValues: PayItemSelectContext['toLabeledValues'] = (records) =>
  toValues(records, {
    value: 'globalId',
    label: 'label',
    deleted: ({ deleted }) => deleted,
  })
/**
 * @see {@link PayItemSelectContext.fromLabeledValues}
 */
const fromLabeledValues: PayItemSelectContext['fromLabeledValues'] = (values) =>
  (values ?? []).map(({ value }) => value as string)

const fromLabeledValue: PayItemSelectContext['fromLabeledValue'] = (
  labeledValue
) => labeledValue?.value as string

const toLabeledValue: PayItemSelectContext['toLabeledValue'] = (record) =>
  record ? toLabeledValues([record])[0] : undefined

const { Provider, useContextFn } = createOptionalContext<PayItemSelectContext>({
  displayName: 'PayItemSelectContext',
  defaults: {
    payItems: [],
    __initialized: false,
    loading: false,
    options: [],
    toLabeledValues,
    fromLabeledValues,
    fromLabeledValue,
    toLabeledValue,
  },
  usingDefaults: ({ __initialized }) => !__initialized,
})

/**
 * returns the context associated with the {@link PayItemSelectProvider}
 *
 * warns if the PayItemSelectProvider is not found
 */
export const usePayItemSelectContext = useContextFn

/**
 * a provider to give access to data and methods associated with the PayItemSelect
 *
 * @param props
 */
const PayItemSelectProvider: React.FC = ({ children }) => {
  const {
    state: { projectGlobalId: globalId },
  } = useGlobalState()
  const { data, loading } = usePayItemSelectPayItemNoGeometriesQuery({
    ...DefaultQueryHookOptions,
    skip: !globalId,
    variables: { globalId: globalId! },
  })
  const options = useOptions({
    items: data?.payItemNoGeometries?.records,
    value: 'globalId',
    label: 'label',
    sort: 'label',
  })
  return (
    <Provider
      value={{
        payItems: data?.payItemNoGeometries?.records ?? [],
        loading,
        options,
        toLabeledValues,
        fromLabeledValues,
        toLabeledValue,
        fromLabeledValue,
        __initialized: true,
      }}
    >
      {children}
    </Provider>
  )
}

export default PayItemSelectProvider
